// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The scanner.
 *
 * One pass over the decoded units, producing findings that carry both a code-point position and an
 * exact byte span. Everything here is a pure function of (units, context) — no DOM, no worker, no
 * globals — which is what lets the whole engine be tested against fixtures.
 *
 * The organising principle, and the thing that separates this from the fifteen textarea cleaners:
 * SEVERITY IS A PROPERTY OF THE OCCURRENCE, NOT OF THE CODE POINT. U+200D between two emoji is
 * `expected` and is never touched; the same U+200D inside a Latin identifier is `suspicious`.
 * U+3000 in a Japanese document is the ordinary space; in a YAML file it is why the parse failed.
 */

import { generalCategory, isExtendedPictographic, scriptOf } from '../data/unicode';
import type { Unit } from '../core/decode';
import {
  BIDI_CLOSE_EMBED,
  BIDI_CLOSE_ISOLATE,
  BIDI_MARKS,
  BIDI_NAMES,
  BIDI_OPEN_EMBED,
  BIDI_OPEN_ISOLATE,
  INVISIBLE_MATH,
  RGI_TAG_SEQUENCES,
  SPACE_LIKE,
  TAG_ASCII_END,
  TAG_ASCII_START,
  TAG_BLOCK_START,
  TAG_CANCEL,
  TAG_LANGUAGE,
  TYPOGRAPHIC,
  VS_BMP_END,
  VS_BMP_START,
  VS_SUP_END,
  VS_SUP_START,
  categoryOf,
  isCandidate,
  isTagChar,
  isVariationSelector,
} from './catalogue';
import type { Finding, FileKind, ScanContext, ScanResult, Severity } from './types';

/* ── context ────────────────────────────────────────────────────────────────────────────────── */

const EXT_BY_KIND: Record<string, FileKind> = {};
const put = (kind: FileKind, exts: string[]): void => {
  for (const e of exts) EXT_BY_KIND[e] = kind;
};
put('code', [
  'js', 'mjs', 'cjs', 'ts', 'tsx', 'jsx', 'py', 'rb', 'go', 'rs', 'java', 'c', 'h', 'cc', 'cpp',
  'hpp', 'cs', 'php', 'sh', 'bash', 'zsh', 'fish', 'sql', 'swift', 'kt', 'kts', 'scala', 'lua',
  'pl', 'pm', 'r', 'm', 'mm', 'gradle', 'dart', 'ex', 'exs', 'erl', 'hs', 'clj', 'vim', 'ps1',
]);
put('data', [
  'json', 'jsonl', 'ndjson', 'yaml', 'yml', 'toml', 'csv', 'tsv', 'ini', 'cfg', 'conf', 'env',
  'properties', 'lock', 'plist', 'makefile', 'mk', 'dockerfile',
]);
put('markup', ['html', 'htm', 'xhtml', 'xml', 'svg', 'vue', 'svelte']);
put('prose', ['md', 'markdown', 'mdx', 'txt', 'rst', 'adoc', 'asciidoc', 'tex', 'org']);
put('terminal', ['log', 'cast', 'ansi', 'out']);

export function fileKindFor(filename: string): FileKind {
  const base = filename.toLowerCase();
  const dot = base.lastIndexOf('.');
  const ext = dot >= 0 ? base.slice(dot + 1) : base;
  return EXT_BY_KIND[ext] ?? 'unknown';
}

/**
 * Which scripts is this document actually written in?
 *
 * Needed so that Persian ZWNJ, Japanese ideographic spaces and Korean jamo fillers are read as the
 * language features they are rather than as attacks. A script counts as dominant when it accounts
 * for at least 5% of the letters — low enough to catch a genuinely bilingual document, high enough
 * that one stray Cyrillic character in a page of English does not make Cyrillic "dominant" and
 * silence the homoglyph check.
 *
 * A CHARACTER CHARSIGHT WOULD FLAG DOES NOT GET A VOTE. This is not a refinement, it is load
 * bearing: U+3164 HANGUL FILLER has Script=Hangul, so in `userㅤname` the one invisible character
 * is 11% of the document and makes Hangul "dominant" — which then excuses it as a plausible Korean
 * jamo filler. The finding would argue itself down to nothing. Caught by a test, which is why the
 * test is there.
 */
export function dominantScripts(units: readonly Unit[]): Set<string> {
  const counts = new Map<string, number>();
  let total = 0;
  for (const u of units) {
    if (u.cp < 0) continue;
    if (isCandidate(u.cp)) continue;
    const sc = scriptOf(u.cp);
    if (sc === 'Zyyy' || sc === 'Zinh' || sc === 'Zzzz') continue;
    counts.set(sc, (counts.get(sc) ?? 0) + 1);
    total++;
  }
  const out = new Set<string>();
  if (total === 0) return out;
  for (const [sc, n] of counts) if (n / total >= 0.05) out.add(sc);
  return out;
}

const ARABIC_SCRIPTS = new Set(['Arab', 'Syrc', 'Thaa', 'Nkoo', 'Adlm', 'Mand']);
const INDIC_SCRIPTS = new Set([
  'Deva', 'Beng', 'Guru', 'Gujr', 'Orya', 'Taml', 'Telu', 'Knda', 'Mlym', 'Sinh', 'Mymr', 'Khmr',
  'Thai', 'Laoo', 'Tibt',
]);
const CJK_SCRIPTS = new Set(['Hani', 'Hira', 'Kana', 'Hang', 'Bopo']);

function hasAny(set: ReadonlySet<string>, of: ReadonlySet<string>): boolean {
  for (const s of set) if (of.has(s)) return true;
  return false;
}

/* ── helpers ────────────────────────────────────────────────────────────────────────────────── */

function span(units: readonly Unit[], from: number, to: number): { start: number; end: number } {
  const a = units[from]!;
  const b = units[to]!;
  return { start: a.offset, end: b.offset + b.length };
}

/** The nearest preceding unit that is not itself a modifier, i.e. the base a selector attaches to. */
function baseBefore(units: readonly Unit[], i: number): Unit | null {
  for (let j = i - 1; j >= 0; j--) {
    const u = units[j]!;
    if (u.cp < 0) return null;
    if (isVariationSelector(u.cp)) continue;
    return u;
  }
  return null;
}

/**
 * Code points that legitimately sit between a zero-width joiner and the emoji it joins.
 *
 * THIS SET IS LOAD BEARING AND THE OBVIOUS RULE IS WRONG. "A ZWJ flanked by Extended_Pictographic
 * is legitimate" sounds right and, measured against Unicode 17's own 1,614 RGI emoji ZWJ sequences,
 * misclassifies 1,391 of them — 86%. The reason is that the characters actually adjacent to the ZWJ
 * are frequently not pictographic at all: the skin-tone modifiers U+1F3FB..U+1F3FF are
 * Emoji_Modifier, not Extended_Pictographic, and U+FE0F is a variation selector. So
 * `👩🏽‍⚕️` has a ZWJ whose left neighbour is a skin tone and whose right neighbour is
 * eventually followed by FE0F, and the naive test flags a perfectly ordinary emoji as an attack.
 *
 * Walking outward past this set first brings the misclassification to zero.
 */
function isEmojiGlue(cp: number): boolean {
  return (
    cp === 0xfe0f ||
    cp === 0xfe0e ||
    (cp >= 0x1f3fb && cp <= 0x1f3ff) || // skin tone modifiers
    (cp >= 0xe0020 && cp <= 0xe007f) // tag characters, for the regional flag sequences
  );
}

/**
 * The script of the characters immediately either side of position `i`.
 *
 * THIS HAS TO BE LOCAL, NOT DOCUMENT-WIDE. A zero-width non-joiner between two Arabic-script
 * letters is orthographically required — Persian needs it to separate a verb prefix from its stem —
 * and that is true whether the surrounding document is Persian or an English README with one
 * Persian example in it. Deciding from the document's dominant script gets the pure cases right and
 * strips a required character out of exactly the mixed case that is most common in practice: a
 * README, in English, quoting one line of another language. Caught by the sample test.
 */
function neighbourScripts(units: readonly Unit[], i: number): Set<string> {
  const out = new Set<string>();
  for (const dir of [-1, 1] as const) {
    for (let j = i + dir; j >= 0 && j < units.length; j += dir) {
      const cp = units[j]!.cp;
      if (cp < 0) break;
      const sc = scriptOf(cp);
      if (sc === 'Zyyy' || sc === 'Zinh' || sc === 'Zzzz') break;
      out.add(sc);
      break;
    }
  }
  return out;
}

/** Step outward from `i` in `dir`, past emoji glue, and report whether we land on a pictograph. */
function pictographBeyond(units: readonly Unit[], i: number, dir: 1 | -1): boolean {
  for (let j = i + dir; j >= 0 && j < units.length; j += dir) {
    const cp = units[j]!.cp;
    if (cp < 0) return false;
    if (isEmojiGlue(cp)) continue;
    return isExtendedPictographic(cp);
  }
  return false;
}

/* ── the scan ───────────────────────────────────────────────────────────────────────────────── */

export function scan(units: readonly Unit[], ctx: ScanContext): ScanResult {
  const findings: Finding[] = [];
  const consumed = new Uint8Array(units.length);
  let invalidBytes = 0;

  const arabicDoc = hasAny(ctx.dominantScripts, ARABIC_SCRIPTS);
  const indicDoc = hasAny(ctx.dominantScripts, INDIC_SCRIPTS);
  const cjkDoc = hasAny(ctx.dominantScripts, CJK_SCRIPTS);
  const hangulDoc = ctx.dominantScripts.has('Hang');
  const codeish = ctx.fileKind === 'code' || ctx.fileKind === 'data';

  /* --- the byte-order mark, which is not a unit and would otherwise never be reported ------- */

  if (ctx.bomLength) {
    findings.push({
      category: 'zero-width',
      severity: 'notable',
      cp: 0xfeff,
      byteStart: 0,
      byteEnd: ctx.bomLength,
      line: 1,
      column: 1,
      runLength: 1,
      reason:
        'A byte-order mark at the very start of the file. That is what a BOM is for, and Excel writes one for UTF-8 CSV — but it also breaks shebang lines, several JSON parsers and CSV header matching, so it is worth knowing it is there.',
      action: 'keep',
    });
  }

  /* --- runs first: tag characters and variation selectors ---------------------------------- */

  for (let i = 0; i < units.length; i++) {
    const u = units[i]!;
    if (u.cp < 0 || !isTagChar(u.cp)) continue;

    let j = i;
    while (j + 1 < units.length && units[j + 1]!.cp >= 0 && isTagChar(units[j + 1]!.cp)) j++;

    // The three RGI emoji tag sequences are the only lawful use of this block anywhere in Unicode.
    // Matched as EXACT full sequences including the U+1F3F4 base — never as "a black flag followed
    // by tag characters", which renders as a bare black flag and is a known hiding place.
    const base = i > 0 ? units[i - 1]!.cp : -1;
    const runCps = units.slice(i, j + 1).map((x) => x.cp);
    const isRgiFlag = RGI_TAG_SEQUENCES.some(
      (seq) =>
        seq[0] === base &&
        seq.length - 1 === runCps.length &&
        seq.slice(1).every((c, k) => c === runCps[k]),
    );

    const s = span(units, i, j);
    for (let k = i; k <= j; k++) consumed[k] = 1;

    if (isRgiFlag) {
      findings.push({
        category: 'tags',
        severity: 'expected',
        cp: -1,
        byteStart: s.start,
        byteEnd: s.end,
        line: u.line,
        column: u.column,
        runLength: runCps.length,
        reason:
          'A registered emoji tag sequence — one of the three regional flags (England, Scotland, Wales) that are the only lawful use of this block.',
        action: 'keep',
      });
      i = j;
      continue;
    }

    const decoded = decodeTagRun(runCps);
    findings.push({
      category: 'tags',
      severity: 'payload',
      cp: -1,
      byteStart: s.start,
      byteEnd: s.end,
      line: u.line,
      column: u.column,
      runLength: runCps.length,
      reason:
        decoded.text.length > 0
          ? `${runCps.length} tag characters spelling hidden text. They render as nothing, and a language model reads them as ordinary instructions.`
          : `${runCps.length} tag-block code points with no ASCII meaning (reserved, or the language/cancel delimiters). Their presence is itself anomalous.`,
      decoded: decoded.text,
      action: 'remove',
    });
    i = j;
  }

  for (let i = 0; i < units.length; i++) {
    const u = units[i]!;
    if (u.cp < 0 || consumed[i] || !isVariationSelector(u.cp)) continue;

    let j = i;
    while (j + 1 < units.length && units[j + 1]!.cp >= 0 && isVariationSelector(units[j + 1]!.cp)) {
      j++;
    }
    const runCps = units.slice(i, j + 1).map((x) => x.cp);
    const s = span(units, i, j);
    for (let k = i; k <= j; k++) consumed[k] = 1;
    const base = baseBefore(units, i);

    // A run of ONE is the discriminator that matters. U+FE0F on an emoji base is the presentation
    // selector — byte 0x0F in the smuggling scheme encodes to that very same code point, so a
    // length-1 run on a valid base has to be read as presentation, not payload. Likewise a single
    // supplementary selector after a Han ideograph is almost certainly a registered ideographic
    // variation sequence from the IVD, which Japanese registry and typography data is full of.
    if (runCps.length === 1) {
      const cp = runCps[0]!;
      const emojiPresentation =
        (cp === 0xfe0f || cp === 0xfe0e) && base !== null && isExtendedPictographic(base.cp);
      const ivs = cp >= VS_SUP_START && cp <= VS_SUP_END && base !== null && scriptOf(base.cp) === 'Hani';
      if (emojiPresentation || ivs) {
        findings.push({
          category: 'vs-smuggling',
          severity: 'expected',
          cp,
          byteStart: s.start,
          byteEnd: s.end,
          line: u.line,
          column: u.column,
          runLength: 1,
          reason: emojiPresentation
            ? 'An emoji presentation selector on an emoji base — this is what makes the character render in colour rather than as text. Removing it changes what you see.'
            : 'A single supplementary variation selector after a Han ideograph: a registered ideographic variation sequence, used throughout Japanese typography and registry data.',
          action: 'keep',
        });
        i = j;
        continue;
      }
    }

    const payload = decodeVariationRun(runCps);
    const severity: Severity = runCps.length >= 2 ? 'payload' : 'suspicious';
    findings.push({
      category: 'vs-smuggling',
      severity,
      cp: runCps.length === 1 ? runCps[0]! : -1,
      byteStart: s.start,
      byteEnd: s.end,
      line: u.line,
      column: u.column,
      runLength: runCps.length,
      reason:
        runCps.length >= 2
          ? `${runCps.length} variation selectors in a row after ${base ? describeBase(base.cp) : 'nothing'}. Each one encodes a byte, so this is ${runCps.length} bytes of hidden data riding on a single visible character.`
          : `A variation selector with no valid base to attach to${base ? '' : ' — there is no preceding character at all'}. It renders as nothing and carries one byte.`,
      decoded: payload.text,
      decodedHex: payload.hex,
      action: 'remove',
    });
    i = j;
  }

  /* --- bidi pairing ------------------------------------------------------------------------- */

  findings.push(...scanBidi(units, consumed));

  /* --- everything else, one code point at a time -------------------------------------------- */

  let combiningRun = 0;
  let combiningStart = -1;

  for (let i = 0; i < units.length; i++) {
    const u = units[i]!;

    if (u.cp < 0) {
      invalidBytes += u.length;
      findings.push({
        category: 'invalid-byte',
        severity: 'notable',
        cp: -1,
        byteStart: u.offset,
        byteEnd: u.offset + u.length,
        line: u.line,
        column: u.column,
        runLength: 1,
        reason:
          'A byte that is not valid in this encoding. Usually the file is really Windows-1252 or Latin-1 rather than UTF-8 — an encoding problem, not hidden text. It is left exactly as it is.',
        action: 'keep',
      });
      continue;
    }

    // Zalgo. The threshold is deliberately high: Vietnamese, Thai, pointed Hebrew, vocalised Arabic
    // and Devanagari routinely stack two to four marks on one base, and a lower threshold fires
    // constantly on real language data.
    const gc = combiningCategory(u.cp);
    if (gc) {
      if (combiningRun === 0) combiningStart = i;
      combiningRun++;
    } else {
      if (combiningRun >= 6) {
        const s = span(units, combiningStart, i - 1);
        findings.push({
          category: 'combining',
          severity: 'notable',
          cp: -1,
          byteStart: s.start,
          byteEnd: s.end,
          line: units[combiningStart]!.line,
          column: units[combiningStart]!.column,
          runLength: combiningRun,
          reason: `${combiningRun} combining marks stacked on one character. Long runs overflow the line and can cover the text around them.`,
          action: 'keep',
        });
      }
      combiningRun = 0;
    }

    if (consumed[i] || !isCandidate(u.cp)) continue;
    const cat = categoryOf(u.cp);
    if (!cat) continue;

    const isFirstUnit = i === 0;

    let severity: Severity = 'suspicious';
    let action: Finding['action'] = 'remove';
    let replacement: string | undefined;
    let reason = '';

    switch (cat) {
      case 'zero-width': {
        if (u.cp === 0x200d) {
          if (pictographBeyond(units, i, -1) && pictographBeyond(units, i, 1)) {
            severity = 'expected';
            action = 'keep';
            reason =
              'A zero-width joiner between two emoji — this is what makes them a single emoji. Removing it turns one family into three separate people.';
            break;
          }
          if (indicDoc || hasAny(neighbourScripts(units, i), INDIC_SCRIPTS)) {
            severity = 'expected';
            action = 'keep';
            reason =
              'A zero-width joiner between Indic-script letters, where it forces a half-form and carries meaning. Removing it changes the spelling.';
            break;
          }
          reason =
            'A zero-width joiner outside any emoji or Indic context. Invisible, and it breaks string equality and search.';
          break;
        }
        if (u.cp === 0x200c) {
          const near = neighbourScripts(units, i);
          if (arabicDoc || hasAny(near, ARABIC_SCRIPTS)) {
            severity = 'expected';
            action = 'keep';
            reason =
              'A zero-width non-joiner between Arabic-script letters, where it is orthographically required — Persian needs it to separate a verb prefix from its stem. Removing it is not cleaning, it is misspelling.';
            break;
          }
          if (indicDoc || hasAny(near, INDIC_SCRIPTS)) {
            severity = 'expected';
            action = 'keep';
            reason =
              'A zero-width non-joiner between Indic-script letters, where it forces an explicit halant instead of a conjunct. It carries meaning.';
            break;
          }
          reason =
            'A zero-width non-joiner outside any script that needs one. Invisible, and it breaks string equality and search.';
          break;
        }
        if (u.cp === 0xfeff) {
          if (isFirstUnit) {
            severity = 'notable';
            action = 'keep';
            reason =
              'A byte-order mark at the very start of the file. That is what a BOM is for, and Excel writes one for UTF-8 CSV — but it also breaks shebang lines, some JSON parsers and CSV header matching.';
            break;
          }
          reason =
            'A zero-width no-break space in the middle of the file. At offset zero this would be a byte-order mark; anywhere else it is an invisible character that most tools will not show you.';
          break;
        }
        if (u.cp === 0x034f) {
          severity = 'notable';
          action = 'keep';
          reason =
            'A combining grapheme joiner. It legitimately blocks canonical reordering and is used in collation tailoring, so it is reported rather than removed.';
          break;
        }
        if (INVISIBLE_MATH.has(u.cp)) {
          severity = 'notable';
          action = 'keep';
          reason =
            'An invisible mathematical operator. Correct and intended in MathML and in technical plain text.';
          break;
        }
        reason =
          'A zero-width character. It survives copy and paste, and it breaks string equality, CSV parsing, JSON keys and diffs.';
        break;
      }

      case 'whitespace': {
        replacement = SPACE_LIKE.get(u.cp);
        action = 'replace';
        if (u.cp === 0x3000 && cjkDoc) {
          severity = 'expected';
          action = 'keep';
          reason =
            'An ideographic space in a CJK document — the ordinary space of Japanese and Chinese and the standard paragraph indent. Replacing it with an ASCII space halves its width.';
          break;
        }
        if (u.cp === 0x2007) {
          severity = 'notable';
          action = 'keep';
          reason =
            'A figure space: exactly as wide as a digit, and it exists to align numeric columns. Replacing it with an ordinary space destroys the alignment it was put there to create.';
          break;
        }
        if ((u.cp === 0x00a0 || u.cp === 0x202f) && (ctx.fileKind === 'prose' || ctx.fileKind === 'markup')) {
          severity = 'notable';
          action = 'keep';
          reason =
            u.cp === 0x202f
              ? 'A narrow no-break space. It is correct French typography before ; : ! ? and inside guillemets, and word processors and content systems insert it automatically.'
              : 'A no-break space. In prose this is usually deliberate — keeping a number with its unit, or a name with its initial.';
          break;
        }
        severity = codeish ? 'suspicious' : 'notable';
        reason = codeish
          ? 'A space that is not a space, in a file where that almost always means a command that will not run, a column that will not match, or a parse that fails.'
          : 'A space character that is not U+0020. It looks identical and does not compare equal.';
        break;
      }

      case 'soft-hyphen': {
        if (ctx.fileKind === 'prose' || ctx.fileKind === 'markup') {
          severity = 'notable';
          action = 'keep';
          reason =
            'A soft hyphen: a permitted hyphenation point, legitimate and common in German, Dutch and Finnish compounds and in justified HTML. Reported rather than stripped, because this is prose.';
          break;
        }
        reason =
          'A soft hyphen. Invisible unless a line break falls on it, and it silently breaks identifier equality, URL comparison and grep — which also makes it a way to split a keyword past a filter.';
        break;
      }

      case 'control': {
        if (u.cp === 0x1b && ctx.fileKind === 'terminal') {
          severity = 'notable';
          action = 'keep';
          reason =
            'An escape character in a terminal recording or log, where escape sequences are the entire point of the file.';
          break;
        }
        if (u.cp >= 0x80 && u.cp <= 0x9f) {
          severity = 'notable';
          action = 'keep';
          reason =
            'A C1 control. In practice this nearly always means the file is really Windows-1252 or Latin-1 and has been read as UTF-8 — an encoding problem, not hidden text.';
          break;
        }
        if (u.cp === 0x0c) {
          severity = 'notable';
          action = 'keep';
          reason =
            'A form feed. A legitimate section separator in Emacs Lisp and in older C sources.';
          break;
        }
        reason =
          u.cp === 0x1b
            ? 'An escape character. This enables terminal escape injection: text that repaints the line, clears it, or fakes a prompt when the file is printed or a log is tailed.'
            : u.cp === 0x00
              ? 'A null byte. It truncates strings in C-based parsers, which is a classic way to smuggle content past a check.'
              : 'A control character. It renders as nothing and confuses parsers and terminals.';
        break;
      }

      case 'pua': {
        severity = 'notable';
        action = 'keep';
        reason =
          'A private-use code point: no assigned meaning, so it renders as nothing, as a box, or — with a custom font — as any glyph at all. Nerd Fonts, Powerline and the Apple logo live here, which is why this is never removed for you.';
        break;
      }

      case 'invisible-filler': {
        if (u.cp === 0x2800) {
          severity = 'notable';
          action = 'keep';
          reason =
            'A blank Braille pattern. It is a printing character with no ink — the classic "empty message" — but in real Braille data it is meaningful, so it is reported rather than removed.';
          break;
        }
        if ((u.cp === 0x115f || u.cp === 0x1160 || u.cp === 0x3164 || u.cp === 0xffa0) && hangulDoc) {
          severity = 'notable';
          action = 'keep';
          reason =
            'A Hangul filler in a Korean document, where it genuinely represents an incomplete syllable block. Plausible here; in a Latin-script username or filename the same character is the classic invisible-name trick.';
          break;
        }
        reason =
          'A character that counts as a letter to identifier rules, username validators and \\w — and renders as nothing. This is how an invisible username or filename is made.';
        break;
      }

      case 'typographic': {
        severity = 'notable';
        action = 'keep';
        replacement = TYPOGRAPHIC.get(u.cp);
        reason =
          'A typographic substitution. It is here because it breaks CSV imports, JSON, shell commands and pasted code — and for no other reason. Word, macOS, iOS keyboards and every content system produce these.';
        break;
      }

      case 'line-separator': {
        action = 'replace';
        replacement = '\n';
        severity = codeish ? 'suspicious' : 'notable';
        reason =
          u.cp === 0x0085
            ? 'A NEXT LINE control. Some tools treat it as a line break and some do not, so the same file has two different line counts depending on who reads it.'
            : 'A Unicode line or paragraph separator. It is legal inside a JSON string but was a line terminator in JavaScript before ES2019, so a payload containing one can break the parser that reads it.';
        break;
      }

      case 'interlinear': {
        reason =
          'An interlinear annotation character. Unicode explicitly excludes these from the "default ignorable" property, yet nearly every renderer shows nothing for them — which is exactly why a check built on that property alone misses them.';
        break;
      }

      case 'noncharacter': {
        reason =
          'A noncharacter or unassigned code point. Noncharacters are permanently unassigned and never valid for interchange, so one being here means the file passed through something that should have rejected it.';
        break;
      }

      case 'deprecated': {
        reason =
          'A deprecated format character. Unicode says not to use it, so no conforming process has to honour it — which means two programs reading this file can legitimately disagree about what it says.';
        break;
      }

      default:
        reason = 'An unusual code point.';
    }

    findings.push({
      category: cat,
      severity,
      cp: u.cp,
      byteStart: u.offset,
      byteEnd: u.offset + u.length,
      line: u.line,
      column: u.column,
      runLength: 1,
      reason,
      action,
      replacement,
    });
  }

  findings.sort((a, b) => a.byteStart - b.byteStart);
  return {
    findings,
    context: ctx,
    codePoints: units.length,
    invalidBytes,
  };
}

function describeBase(cp: number): string {
  if (isExtendedPictographic(cp)) return 'an emoji';
  return `the character ${String.fromCodePoint(cp)}`;
}

/** Mn, Mc or Me — a mark that attaches to the preceding base. */
function combiningCategory(cp: number): boolean {
  // The hot path walks every code point in the file, and no combining mark exists below U+0300.
  if (cp < 0x300) return false;
  const gc = generalCategory(cp);
  return gc === 'Mn' || gc === 'Mc' || gc === 'Me';
}

/* ── tag decoding ───────────────────────────────────────────────────────────────────────────── */

/**
 * Decode a run of Plane-14 tag characters.
 *
 * The mapping is a pure offset: U+E0020..U+E007E are printable ASCII 0x20..0x7E shifted up by
 * 0xE0000. U+E0001 LANGUAGE TAG and U+E007F CANCEL TAG are the historical delimiters and have no
 * ASCII counterpart.
 *
 * The delimiters are NOT required. Real payloads routinely omit both — the public encoders emit
 * bare tag runs — so a decoder that only matches delimited sequences misses most real cases.
 */
export function decodeTagRun(cps: readonly number[]): { text: string; reserved: number } {
  let text = '';
  let reserved = 0;
  for (const cp of cps) {
    if (cp >= TAG_ASCII_START && cp <= TAG_ASCII_END) text += String.fromCharCode(cp - TAG_BLOCK_START);
    else if (cp === TAG_LANGUAGE || cp === TAG_CANCEL) continue;
    else reserved++;
  }
  return { text, reserved };
}

/* ── variation-selector decoding ────────────────────────────────────────────────────────────── */

/**
 * Decode a run of variation selectors to the bytes it carries.
 *
 * There are exactly 256 selectors — 16 in U+FE00..U+FE0F and 240 in U+E0100..U+E01EF — which is
 * exactly one byte of range, and the published scheme is the single contiguous index:
 *   byte < 16  -> U+FE00 + byte;  byte >= 16 -> U+E0100 + (byte - 16)
 *
 * There is no length prefix and no terminator, so decoding is "consume the maximal run". The hex
 * is always reported and the UTF-8 reading only when the bytes are valid UTF-8 — the payload need
 * not be text at all; it can be a key or a serialised blob.
 *
 * The Mongolian free variation selectors U+180B..U+180F are deliberately NOT folded in here: they
 * are a different range, they are required by real Mongolian orthography, and the scheme does not
 * use them.
 */
export function decodeVariationRun(cps: readonly number[]): { hex: string; text: string } {
  const bytes: number[] = [];
  for (const cp of cps) {
    if (cp >= VS_BMP_START && cp <= VS_BMP_END) bytes.push(cp - VS_BMP_START);
    else if (cp >= VS_SUP_START && cp <= VS_SUP_END) bytes.push(cp - VS_SUP_START + 16);
  }
  const hex = bytes.map((b) => b.toString(16).padStart(2, '0')).join(' ');
  let text = '';
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(bytes));
  } catch {
    text = '';
  }
  return { hex, text };
}

/* ── bidi ───────────────────────────────────────────────────────────────────────────────────── */

interface BidiFrame {
  index: number;
  cp: number;
  kind: 'embed' | 'isolate';
}

/**
 * Control-character pairing analysis. **Not** an implementation of the Unicode Bidirectional
 * Algorithm, and the UI says so in those words.
 *
 * This is the GCC `-Wbidi-chars=unpaired` check: a stack, not the UBA. An unterminated opener or an
 * unmatched closer means the directional effect leaks past the boundary a human believes exists —
 * the end of a comment, the end of a string literal, the end of the line — which is the Trojan
 * Source signature. Balanced isolates around a Latin name inside Arabic prose are the practice
 * UAX #9 explicitly recommends, and flagging those is pure noise.
 *
 * The atom is the LINE. charsight is file-type-agnostic and does not pretend to tokenize arbitrary
 * source, so the boundary it checks against is the one boundary every file has.
 */
export function scanBidi(units: readonly Unit[], consumed: Uint8Array): Finding[] {
  const out: Finding[] = [];
  let stack: BidiFrame[] = [];
  let maxDepthReported = false;
  let line = units.length > 0 ? units[0]!.line : 1;

  const flushLine = (): void => {
    for (const f of stack) {
      const u = units[f.index]!;
      consumed[f.index] = 1;
      out.push({
        category: 'bidi',
        severity: 'suspicious',
        cp: f.cp,
        byteStart: u.offset,
        byteEnd: u.offset + u.length,
        line: u.line,
        column: u.column,
        runLength: 1,
        reason: `An unterminated ${BIDI_NAMES.get(f.cp) ?? 'directional control'}. It is never closed on this line, so its effect on what a human sees runs past the end of the comment, string or line the writer appears to have finished.`,
        action: 'remove',
      });
    }
    stack = [];
  };

  for (let i = 0; i < units.length; i++) {
    const u = units[i]!;
    if (u.cp < 0) continue;
    if (u.line !== line) {
      flushLine();
      line = u.line;
    }

    if (BIDI_MARKS.has(u.cp)) {
      consumed[i] = 1;
      out.push({
        category: 'bidi',
        severity: 'notable',
        cp: u.cp,
        byteStart: u.offset,
        byteEnd: u.offset + u.length,
        line: u.line,
        column: u.column,
        runLength: 1,
        reason: `A ${BIDI_NAMES.get(u.cp)} mark. These never pair, and they are the normal way to pin the position of punctuation, digits and file paths in mixed-direction text.`,
        action: 'keep',
      });
      continue;
    }

    if (BIDI_OPEN_EMBED.has(u.cp)) {
      stack.push({ index: i, cp: u.cp, kind: 'embed' });
    } else if (BIDI_OPEN_ISOLATE.has(u.cp)) {
      stack.push({ index: i, cp: u.cp, kind: 'isolate' });
    } else if (u.cp === BIDI_CLOSE_EMBED || u.cp === BIDI_CLOSE_ISOLATE) {
      const want = u.cp === BIDI_CLOSE_EMBED ? 'embed' : 'isolate';
      let k = stack.length - 1;
      // UAX #9 rule X6a: a PDI also terminates any embeddings opened inside the isolate it closes.
      while (k >= 0 && stack[k]!.kind !== want) k--;
      if (k < 0) {
        consumed[i] = 1;
        out.push({
          category: 'bidi',
          severity: 'suspicious',
          cp: u.cp,
          byteStart: u.offset,
          byteEnd: u.offset + u.length,
          line: u.line,
          column: u.column,
          runLength: 1,
          reason: `An unmatched ${BIDI_NAMES.get(u.cp)}. It closes a directional scope that was never opened on this line, which means the scope it really closes was opened somewhere the reader cannot see.`,
          action: 'remove',
        });
      } else {
        // Everything above the matched frame is discarded, exactly as the algorithm would.
        const closed = stack.splice(k);
        for (const f of closed) {
          consumed[f.index] = 1;
          const uu = units[f.index]!;
          out.push({
            category: 'bidi',
            severity: 'expected',
            cp: f.cp,
            byteStart: uu.offset,
            byteEnd: uu.offset + uu.length,
            line: uu.line,
            column: uu.column,
            runLength: 1,
            reason: `A balanced ${BIDI_NAMES.get(f.cp)}. It is closed on the same line, which is how correct bidirectional text is written.`,
            action: 'keep',
          });
        }
        consumed[i] = 1;
      }
    } else {
      continue;
    }

    if (stack.length > 125 && !maxDepthReported) {
      maxDepthReported = true;
      out.push({
        category: 'bidi',
        severity: 'suspicious',
        cp: u.cp,
        byteStart: u.offset,
        byteEnd: u.offset + u.length,
        line: u.line,
        column: u.column,
        runLength: 1,
        reason:
          'Directional controls nested more than 125 deep. Past that limit the Unicode algorithm simply ignores further pushes, so what renders and what the text says diverge by design.',
        action: 'remove',
      });
    }
  }
  flushLine();
  return out;
}
